import { WhooshThemeColors } from "@whoosh/design-system/src/theme/restyle/theme";

import { DateStatus } from "../types";

type BaseColors = {
  Available: { surface: WhooshThemeColors; outline: WhooshThemeColors };
  Disabled: { surface: WhooshThemeColors; outline: WhooshThemeColors };
  Unavailable: { surface: WhooshThemeColors; outline: WhooshThemeColors };
  AvailabilityLoading: {
    surface: WhooshThemeColors;
    outline: WhooshThemeColors;
  };
  InRange: { surface: WhooshThemeColors; outline: WhooshThemeColors };
  Default: { surface: WhooshThemeColors; outline: WhooshThemeColors };
};

type DateButtonColors = {
  neutral: BaseColors;
  pressing: BaseColors;
  hovering: BaseColors;
};

const dateButtonColors: DateButtonColors = {
  neutral: {
    Available: {
      surface: "surface-status-success-subtlest",
      outline: "surface-status-success-subtlest",
    },
    Disabled: {
      surface: "button-white-surface-neutral",
      outline: "button-white-surface-neutral",
    },
    Unavailable: {
      surface: "surface-status-error-subtlest",
      outline: "surface-status-error-subtlest",
    },
    AvailabilityLoading: {
      surface: "surface-secondary",
      outline: "surface-secondary",
    },
    InRange: { surface: "surface-secondary", outline: "surface-secondary" },
    Default: { surface: "surface-primary", outline: "surface-primary" },
  },
  pressing: {
    Available: {
      surface: "surface-status-success-subtle",
      outline: "surface-status-success-subtle",
    },
    Disabled: {
      surface: "button-white-surface-neutral",
      outline: "button-white-surface-neutral",
    },
    Unavailable: {
      surface: "surface-status-error-subtle",
      outline: "surface-status-error-subtle",
    },
    AvailabilityLoading: {
      surface: "surface-secondary",
      outline: "surface-secondary",
    },
    InRange: {
      surface: "button-white-surface-pressed",
      outline: "outline-default",
    },
    Default: {
      surface: "button-white-surface-pressed",
      outline: "outline-default",
    },
  },
  hovering: {
    Available: {
      surface: "surface-status-success-subtler",
      outline: "surface-status-success-subtler",
    },
    Disabled: {
      surface: "button-white-surface-neutral",
      outline: "button-white-surface-neutral",
    },
    Unavailable: {
      surface: "surface-status-error-subtler",
      outline: "surface-status-error-subtler",
    },
    AvailabilityLoading: {
      surface: "surface-secondary",
      outline: "surface-secondary",
    },
    InRange: {
      surface: "button-white-surface-hover",
      outline: "outline-default",
    },
    Default: {
      surface: "button-white-surface-hover",
      outline: "outline-default",
    },
  },
};

export const getBackroundRangeColoring = (
  selectedDates: string[] | undefined,
  hoveredDate: string | null,
  displayedDate: string,
  status: DateStatus,
  discreteMultiSelect = false
): {
  before: boolean;
  after: boolean;
  colors: {
    neutral: { surface: WhooshThemeColors; outline: WhooshThemeColors };
    hovering: { surface: WhooshThemeColors; outline: WhooshThemeColors };
    pressing: { surface: WhooshThemeColors; outline: WhooshThemeColors };
  };
} => {
  let internalStatus = status;
  let before = false;
  let after = false;

  if (discreteMultiSelect) {
    return {
      before: false,
      after: false,
      colors: {
        neutral: dateButtonColors.neutral[status],
        hovering: dateButtonColors.hovering[status],
        pressing: dateButtonColors.pressing[status],
      },
    };
  }

  if (selectedDates?.length && (hoveredDate || selectedDates.length >= 2)) {
    const firstSelectedDate = selectedDates[0];
    const secondDate = selectedDates[1] ?? hoveredDate;

    const startDate =
      firstSelectedDate < secondDate ? firstSelectedDate : secondDate;
    const endDate =
      firstSelectedDate > secondDate ? firstSelectedDate : secondDate;

    if (displayedDate > startDate && displayedDate < endDate) {
      internalStatus = "InRange";
      before = true;
      after = true;
    } else if (displayedDate === startDate || displayedDate === endDate) {
      if (displayedDate === startDate) {
        after = true;
      } else {
        before = true;
      }
    }
  }

  return {
    before,
    after,
    colors: {
      neutral: dateButtonColors.neutral[internalStatus],
      hovering: dateButtonColors.hovering[internalStatus],
      pressing: dateButtonColors.pressing[internalStatus],
    },
  };
};
